// Imports
import { faCheck, faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components/macro';
import plansDetails from '../../../../shareables/front-end/subscription-plans/plans-details';
import Button from '../../../resources/button';


// Styled components
const Container = styled.div<{ backgroundColor: string }>`
	background-color: ${({ backgroundColor }) => backgroundColor};
	border-radius: 1.25rem;
	padding: 1.875rem;
	margin: 1.875rem auto 0;
	width: 100%;
	max-width: 45rem;
	
	display: grid;
	grid-template-columns: 8.9375rem 10.8125rem 12.9375rem;
	justify-content: space-between;
	
	@media (max-width: 48rem) {
		display: flex;
		flex-direction: column;
		max-width: 35.5rem;
		gap: 0;
	}
`;

const Column = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

const Heading = styled.h2<{ color: string }>`
	color: ${({ color }) => color};
	font-size: 1.875rem;
	font-weight: 600;
	line-height: 1;
`;

const Description = styled.span`
	font-size: 1rem;
	line-height: 1.2;
	margin-bottom: 0.375rem !important;
`;

const Features = styled.ul`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
	margin: auto 0;
	
	@media (max-width: 48rem) {
		margin-top: 0.625rem;
	}
`;

const Feature = styled.li`
	display: flex;
	align-items: center;
	gap: 0.625rem;
	white-space: nowrap;
`;

const IconLayers = styled.span`
	position: absolute;
	top: 0;
	left: -0.129rem;
	
	font-size: 1.25rem;
`;

const Checkbox = styled.div`
	position: relative;
	
	width: 1.25rem;
	height: 1.25rem;
	min-width: 1.25rem;
`;

const DesktopButton = styled(Button)`
	width: fit-content;
	font-size: 1rem;
	white-space: nowrap;
	height: 2.5rem;
	
	@media (max-width: 48rem) {
		display: none;
	}
`;

const MobileButton = styled(Button)`
	width: 100%;
	margin-top: 1rem;
	justify-content: center;
	
	@media (min-width: 48.0625rem) {
		display: none;
	}
`;


// Function component
const FreePlan: React.FC = () => {
	// Destructure
	const { name, description, backgroundColor, accentColor, features } = plansDetails.freePlan;
	
	
	// Split feature list in half
	//  -> This helps reduce code redundancy when rendering the feature columns
	const midpoint = Math.ceil(features.features.length / 2);
	const featureLists = [features.features.slice(0, midpoint), features.features.slice(midpoint)];
	
	
	// Initialize
	const { callToActionText, ...linkButtonProps } = {
		callToActionText: 'Get started',
		backgroundColor: 'hsl(36, 44%, 52%)',
		fontColor: 'hsl(0, 0%, 100%)',
		href: `https://start.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}`,
		displayArrowIndicator: true,
	};
	
	
	// Return JSX
	return (
		<Container backgroundColor={backgroundColor}>
			<Column>
				<Heading color={accentColor}>{name}</Heading>
				<Description>{description}</Description>
				<DesktopButton {...linkButtonProps}>{callToActionText}</DesktopButton>
			</Column>
			
			{featureLists.map((featureList, index) => (
				<Column key={index}>
					<Features>
						{featureList.map(({ text: featureText }) => (
							<Feature key={featureText}>
								<Checkbox>
									<IconLayers className='fa-layers fa-fw'>
										<FontAwesomeIcon icon={faCircle} color={accentColor} />
										<FontAwesomeIcon icon={faCheck} color='hsl(0, 0%, 100%)' transform='shrink-6.36' />
									</IconLayers>
								</Checkbox>
								{featureText}
							</Feature>
						))}
					</Features>
				</Column>
			))}
			
			<MobileButton {...linkButtonProps}>{callToActionText}</MobileButton>
		</Container>
	);
};

export default FreePlan;
